const normalizeDigits = (value) => value.replace(/\D/g, '');
const wishlistStorageKey = 'maryssil-wishlist-v1';
const headerScrollThreshold = 40;

let headerScrollCleanup = null;
let dialogBackdropInitialized = false;

function initializeMaryssilHeader() {
  headerScrollCleanup?.();
  headerScrollCleanup = null;

  const header = document.querySelector('#header-component');
  if (!(header instanceof HTMLElement)) return;

  const isHome = document.body.classList.contains('template-index');
  if (!isHome) {
    header.classList.remove('maryssil-header--scrolled');
    document.body.style.removeProperty('--maryssil-home-header-offset');
    return;
  }

  const pageWrapper = document.querySelector('.page-wrapper');
  const headerGroup = document.querySelector('#header-group');
  const headerSection = header.closest('.header-section');
  const syncHomeHeaderOffset = () => {
    if (!(headerGroup instanceof HTMLElement)) return;

    const offset = Array.from(headerGroup.children).reduce((height, section) => {
      if (!(section instanceof HTMLElement) || section === headerSection) return height;
      return height + section.getBoundingClientRect().height;
    }, 0);

    document.body.style.setProperty('--maryssil-home-header-offset', `${Math.round(offset)}px`);
  };
  const updateHeader = () => {
    const scrollTop = Math.max(
      window.scrollY,
      pageWrapper instanceof HTMLElement ? pageWrapper.scrollTop : 0
    );
    header.classList.toggle('maryssil-header--scrolled', scrollTop > headerScrollThreshold);
  };

  const controller = new AbortController();
  const listenerOptions = { passive: true, signal: controller.signal };
  window.addEventListener('scroll', updateHeader, listenerOptions);
  window.addEventListener('resize', syncHomeHeaderOffset, listenerOptions);
  pageWrapper?.addEventListener('scroll', updateHeader, listenerOptions);
  syncHomeHeaderOffset();
  updateHeader();

  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(syncHomeHeaderOffset)
    : null;
  if (resizeObserver && headerGroup instanceof HTMLElement) {
    Array.from(headerGroup.children).forEach((section) => resizeObserver.observe(section));
  }

  headerScrollCleanup = () => {
    controller.abort();
    resizeObserver?.disconnect();
  };
}

function portalMaryssilHeaderDrawers() {
  for (const id of ['maryssil-account-drawer', 'maryssil-wishlist-drawer']) {
    const drawer = document.getElementById(id);
    if (drawer && drawer.parentElement !== document.body) document.body.append(drawer);
  }
}

function initializeMaryssilDialogBackdrops(root = document) {
  if (dialogBackdropInitialized) return;
  dialogBackdropInitialized = true;
  document.addEventListener('click', (event) => {
    const dialog = document.querySelector(
      '.theme-drawer__dialog[open], #search-modal .search-modal__content[open]',
    );
    if (!(dialog instanceof HTMLDialogElement)) return;

    const owner = dialog.closest('theme-drawer, dialog-component');
    const trigger = event.target instanceof Element
      ? event.target.closest('button, [role="button"], [aria-controls]')
      : null;
    const ownerId = owner?.id;
    const triggerAction = trigger?.getAttribute('on:click');
    if (
      ownerId &&
      (trigger?.getAttribute('aria-controls') === ownerId || triggerAction?.startsWith(`#${ownerId}/`))
    ) {
      return;
    }

    const bounds = dialog.getBoundingClientRect();
    const insidePanel =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;
    if (insidePanel) return;

    if (owner instanceof HTMLElement && typeof owner.close === 'function') {
      owner.close();
      return;
    }

    const closeButton = dialog.querySelector(
      '.theme-drawer__close-button, .maryssil-search-modal__close, [aria-label^="Fechar"]'
    );
    if (closeButton instanceof HTMLElement) closeButton.click();
    else dialog.close();
  });
}

function formatDevStorePricesAsBRL(root = document) {
  if (!document.body?.classList.contains('maryssil-force-brl')) return;
  const selectors = [
    '.price', '.money', '[class*="price"]', '.cart-items', '.cart-drawer', '.cart-drawer__summary',
    '.cart-totals', '.cart__summary-totals', '.cart__summary',
    '.maryssil-paired-prices', '.maryssil-paired-purchase', '.maryssil-shipping-progress', '[data-wishlist-grid]'
  ];
  let scope = root;
  if (root instanceof Element && !root.matches(selectors.join(','))) {
    scope = root.closest(selectors.join(',')) || root;
  }
  const nodes = scope.matches?.(selectors.join(',')) ? [scope] : scope.querySelectorAll?.(selectors.join(',')) || [];
  for (const node of nodes) {
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const text = walker.currentNode;
      const currentValue = text.nodeValue || '';
      if (!/(?:US\$|(?<!R)\$)\s*[\d,]+\.\d{2}/.test(currentValue)) continue;
      const formattedValue = currentValue.replace(/(?:US\$|(?<!R)\$)\s*([\d,]+)\.(\d{2})/g, (_match, integer, decimals) =>
        `R$ ${integer.replace(/,/g, '.')},${decimals}`
      );
      if (formattedValue !== currentValue) text.nodeValue = formattedValue;
    }
  }
}

function readWishlist() {
  try {
    const value = JSON.parse(window.localStorage.getItem(wishlistStorageKey) || '[]');
    return Array.isArray(value) ? value : [];
  } catch (_) {
    return [];
  }
}

function writeWishlist(items) {
  window.localStorage.setItem(wishlistStorageKey, JSON.stringify(items));
  document.dispatchEvent(new CustomEvent('maryssil:wishlist-change', { detail: items }));
}

function productFromWishlistButton(button) {
  return {
    id: button.dataset.productId,
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle,
    url: button.dataset.productUrl,
    image: button.dataset.productImage,
    price: button.dataset.productPrice,
  };
}

function syncWishlistUI(root = document) {
  const items = readWishlist();
  const ids = new Set(items.map((item) => String(item.id)));

  for (const button of root.querySelectorAll('[data-maryssil-wishlist-button]')) {
    const active = ids.has(String(button.dataset.productId));
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', `${active ? 'Remover' : 'Adicionar'} ${button.dataset.productTitle} ${active ? 'dos' : 'aos'} favoritos`);
    button.title = active ? 'Remover dos favoritos' : 'Salvar nos favoritos';
  }

  for (const link of document.querySelectorAll('.maryssil-header-favorites')) {
    link.dataset.count = String(items.length);
    link.setAttribute('aria-label', `Meus favoritos, ${items.length} ${items.length === 1 ? 'item' : 'itens'}`);
  }

  for (const count of document.querySelectorAll('[data-maryssil-mobile-wishlist-count]')) {
    count.textContent = String(items.length);
  }

  renderWishlistPage(items);
}

function renderWishlistPage(items = readWishlist()) {
  for (const page of document.querySelectorAll('[data-maryssil-wishlist-page]')) {
    const grid = page.querySelector('[data-wishlist-grid]');
    const empty = page.querySelector('[data-wishlist-empty]');
    if (!grid || !empty) continue;
    grid.replaceChildren();
    empty.hidden = items.length > 0;

    for (const item of items) {
      const article = document.createElement('article');
      article.className = 'maryssil-wishlist-page__item';
      const mediaLink = document.createElement('a');
      mediaLink.className = 'maryssil-wishlist-page__media';
      mediaLink.href = item.url;
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      mediaLink.append(image);

      const content = document.createElement('div');
      content.className = 'maryssil-wishlist-page__content';
      const title = document.createElement('h2');
      const titleLink = document.createElement('a');
      titleLink.href = item.url;
      titleLink.textContent = item.title;
      title.append(titleLink);
      const price = document.createElement('p');
      price.className = 'maryssil-wishlist-page__price';
      price.textContent = item.price;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'maryssil-wishlist-page__remove';
      remove.textContent = 'Remover';
      remove.addEventListener('click', () => writeWishlist(readWishlist().filter((saved) => String(saved.id) !== String(item.id))));
      content.append(title, price, remove);
      article.append(mediaLink, content);
      grid.append(article);
    }
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-maryssil-wishlist-button]');
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();
  const product = productFromWishlistButton(button);
  const current = readWishlist();
  const exists = current.some((item) => String(item.id) === String(product.id));
  writeWishlist(exists ? current.filter((item) => String(item.id) !== String(product.id)) : [...current, product]);
});

document.addEventListener('maryssil:wishlist-change', () => syncWishlistUI());

document.addEventListener('click', (event) => {
  const mobileWishlist = event.target.closest('[data-maryssil-mobile-wishlist]');
  const mobileLink = event.target.closest('[data-maryssil-mobile-nav-link]');
  if (!mobileWishlist && !mobileLink) return;

  const closeButton = document.querySelector('[data-maryssil-mobile-menu-close]');
  if (mobileWishlist) {
    event.preventDefault();
    closeButton?.click();
    window.setTimeout(() => document.querySelector('.maryssil-header-favorites')?.click(), 240);
    return;
  }

  closeButton?.click();
});

function enhanceExternalLinks(root = document) {
  for (const link of root.querySelectorAll('a[data-maryssil-whatsapp]')) {
    const number = normalizeDigits(link.dataset.number || '');
    const message = link.dataset.message || '';

    if (!number) {
      link.hidden = true;
      continue;
    }

    link.href = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
  }
}

function announceExplicitSelection(picker) {
  if (!(picker instanceof HTMLElement) || picker.dataset.requireExplicitSelection !== 'true') return;

  const requiredControls = picker.querySelectorAll('.variant-option--buttons, select.variant-option__select').length;
  const selectedControls = picker.querySelectorAll('fieldset input:checked, select:valid').length;
  const prompt = picker.querySelector('.maryssil-explicit-selection');
  const complete = requiredControls > 0 && selectedControls >= requiredControls;

  if (prompt instanceof HTMLElement) {
    prompt.hidden = complete;
  }
}

function setMaryssilZoomIndex(dialog, requestedIndex) {
  if (!(dialog instanceof HTMLDialogElement)) return;
  const media = [...dialog.querySelectorAll('.dialog-zoomed-gallery > .product-media-container')];
  if (!media.length) return;

  const index = Math.min(Math.max(Number(requestedIndex) || 0, 0), media.length - 1);
  media.forEach((item, itemIndex) => {
    if (itemIndex === index) item.setAttribute('data-maryssil-active', 'true');
    else item.removeAttribute('data-maryssil-active');
  });
  dialog.dataset.maryssilZoomIndex = String(index);

  const counter = dialog.querySelector('[data-maryssil-zoom-current]');
  if (counter) counter.textContent = String(index + 1).padStart(2, '0');

  const thumbnails = [...dialog.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
  thumbnails.forEach((thumbnail, itemIndex) => thumbnail.setAttribute('aria-selected', String(itemIndex === index)));
}

function setMaryssilZoomState(dialog, zoomed) {
  if (!(dialog instanceof HTMLDialogElement)) return;
  const gallery = dialog.querySelector('.dialog-zoomed-gallery');
  const toggle = dialog.querySelector('[data-maryssil-zoom-toggle]');
  const caption = dialog.querySelector('.maryssil-zoom-caption');
  gallery?.setAttribute('data-maryssil-zoomed', String(zoomed));
  toggle?.setAttribute('aria-pressed', String(zoomed));
  if (toggle) toggle.textContent = zoomed ? 'Ver imagem inteira' : 'Ver detalhes';
  if (caption) caption.textContent = zoomed ? 'Mova o cursor ou o toque para examinar os detalhes' : 'Imagem inteira';
}

function initializeMaryssilZoom(root = document) {
  for (const zoom of root.querySelectorAll('zoom-dialog')) {
    const dialog = zoom.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement) || dialog.dataset.maryssilZoomReady === 'true') continue;
    dialog.dataset.maryssilZoomReady = 'true';

    const media = [...dialog.querySelectorAll('.dialog-zoomed-gallery > .product-media-container')];
    const thumbnails = [...dialog.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
    const activateSelectedMedia = () => {
      const selected = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-selected') === 'true');
      setMaryssilZoomIndex(dialog, selected >= 0 ? selected : Number(dialog.dataset.maryssilPendingIndex || 0));
    };

    new MutationObserver(() => {
      if (dialog.open) {
        setMaryssilZoomState(dialog, false);
        window.requestAnimationFrame(activateSelectedMedia);
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

    thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => setMaryssilZoomIndex(dialog, index)));

    dialog.querySelector('[data-maryssil-zoom-previous]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const current = Number(dialog.dataset.maryssilZoomIndex || 0);
      const index = (current - 1 + media.length) % media.length;
      setMaryssilZoomState(dialog, false);
      setMaryssilZoomIndex(dialog, index);
      thumbnails[index]?.click();
    });

    dialog.querySelector('[data-maryssil-zoom-next]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const current = Number(dialog.dataset.maryssilZoomIndex || 0);
      const index = (current + 1) % media.length;
      setMaryssilZoomState(dialog, false);
      setMaryssilZoomIndex(dialog, index);
      thumbnails[index]?.click();
    });

    dialog.querySelector('[data-maryssil-zoom-toggle]')?.addEventListener('click', () => {
      const gallery = dialog.querySelector('.dialog-zoomed-gallery');
      setMaryssilZoomState(dialog, gallery?.getAttribute('data-maryssil-zoomed') !== 'true');
    });

    /* Inside the open viewer, clicking the image alternates overview/detail
       without closing the dialog. */
    const gallery = dialog.querySelector('.dialog-zoomed-gallery');
    gallery?.addEventListener('click', (event) => {
      event.stopPropagation();
      setMaryssilZoomState(dialog, gallery.getAttribute('data-maryssil-zoomed') !== 'true');
    }, true);
    gallery?.addEventListener('pointermove', (event) => {
      if (gallery.getAttribute('data-maryssil-zoomed') !== 'true') return;
      const bounds = gallery.getBoundingClientRect();
      gallery.style.setProperty('--maryssil-zoom-x', `${((event.clientX - bounds.left) / bounds.width) * 100}%`);
      gallery.style.setProperty('--maryssil-zoom-y', `${((event.clientY - bounds.top) / bounds.height) * 100}%`);
    });
    setMaryssilZoomIndex(dialog, 0);
    setMaryssilZoomState(dialog, false);
  }

  for (const button of root.querySelectorAll('.product-media-container__zoom-button')) {
    if (button.dataset.maryssilZoomTriggerReady === 'true') continue;
    button.dataset.maryssilZoomTriggerReady = 'true';
    button.addEventListener('click', () => {
      const gallery = button.closest('media-gallery');
      const dialog = gallery?.querySelector('zoom-dialog dialog');
      if (!(dialog instanceof HTMLDialogElement)) return;
      const buttons = [...gallery.querySelectorAll('.product-media-container__zoom-button')];
      const index = Math.max(0, buttons.indexOf(button));
      dialog.dataset.maryssilPendingIndex = String(index);
      window.setTimeout(() => setMaryssilZoomIndex(dialog, index), 0);
    });
  }
}

function initializeMaryssilEnhancements(root = document) {
  initializeMaryssilHeader();
  portalMaryssilHeaderDrawers();
  initializeMaryssilDialogBackdrops(root);
  initializeMaryssilZoom(root);
  enhanceExternalLinks(root);
  syncWishlistUI(root);
  formatDevStorePricesAsBRL(root);

  for (const picker of root.querySelectorAll('variant-picker[data-require-explicit-selection="true"]')) {
    announceExplicitSelection(picker);
    picker.addEventListener('change', () => announceExplicitSelection(picker));
  }
}

initializeMaryssilEnhancements();

if (document.body?.classList.contains('maryssil-force-brl')) {
  let brlFrame = 0;
  const brlObserver = new MutationObserver((mutations) => {
    window.cancelAnimationFrame(brlFrame);
    brlFrame = window.requestAnimationFrame(() => {
      for (const mutation of mutations) {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
        if (target) formatDevStorePricesAsBRL(target);
      }
    });
  });
  brlObserver.observe(document.body, { childList: true, characterData: true, subtree: true });
}

document.addEventListener('shopify:section:load', (event) => {
  initializeMaryssilEnhancements(event.target);
});
